import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    IconButton,
    Typography,
    Unstable_Grid2 as Grid,
} from '@mui/material';
import BrandInfo from './BrandInfo';
import LabeledTextList, { LabeledTextListItem } from '@/components/common/LabeledTextList';
import { useLoaderData, useParams, Await, useRevalidator } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
import { ModeEdit as EditIcon, Info as InfoIcon } from '@mui/icons-material';

import { getMallInfo, MallInfoResponse } from '@/services/mall.service';
import { dialogOpen } from '@/modules/MuiDialog';
import { BusinessInfoDialog } from './BusinessInfoDialog';
import { AccountInfoDialog } from './AccountInfoDialog';
import { ReturnAddressInfoDialog } from './ReturnAddressInfoDialog';
import TaxInfoDialog from './TaxInfoDialog';
import { ManagerPhoneDialog } from './ManagerPhoneDialog';
import sessionStore from '@/store/modules/sessionStore';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSpaceUserV2Store } from '@/store';
import CustomerCareBanner from './CustomerCareBanner';

const BusinessInfo = () => {
    const { t } = useTranslation();
    const { brandInfoData, businessData, customerSummary }: any = useLoaderData();
    const { revalidate, state } = useRevalidator();
    const { grade_name } = useSpaceUserV2Store();
    const isSodomall = grade_name === '소도몰';
    const [brandInfo, setBrandInfo] = useState<MallInfoResponse>(brandInfoData);
    const [isDispatching, setIsDispatching] = useState(false);
    const { mallId } = useParams();

    useEffect(() => {
        setBrandInfo(brandInfoData);
    }, [brandInfoData]);

    const dispatchBrandInfo = async () => {
        setIsDispatching(true);
        try {
            const brandInfo = await getMallInfo(mallId || '');
            setBrandInfo(brandInfo);
        } catch (e) {
            console.error(e);
        } finally {
            setIsDispatching(false);
        }
    };
    return (
        <div className={'flex flex-col flex-1'}>
            <Grid component={'section'} container>
                <Grid xs={12}>
                    <Typography variant={'h1'} gutterBottom>
                        {t('mall.sellerInfo')}
                    </Typography>
                </Grid>
                {/* 고객관리 바로가기 배너 — 소도몰 역할에서는 고객관리 메뉴가 숨겨지므로 미노출 */}
                {!isSodomall && (
                    <Grid xs={12}>
                        <CustomerCareBanner summary={customerSummary} />
                    </Grid>
                )}
                <Grid xs={12} md={6}>
                    <BrandInfo default={brandInfo} dispatchDefault={dispatchBrandInfo} />
                </Grid>
                <Grid xs={12} md={6}>
                    <Grid xs={12}>
                        <Suspense fallback={<div>loading...</div>}>
                            <Await resolve={businessData}>
                                {(businessData) => {
                                    const isBlurData = businessData.mallOpenBusinessType === 'PLATFORM';
                                    return (
                                        <Card>
                                            <div className="flex">
                                                <CardHeader
                                                    title={
                                                        <div className={'flex justify-between items-center'}>
                                                            <p>{t('mall.accountInfo')}</p>
                                                            <Box
                                                                sx={{
                                                                    height: '10px',
                                                                    overflowY: 'visible',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <IconButton
                                                                    onClick={() => {
                                                                        const { userId } = sessionStore.getState();
                                                                        if (!userId) {
                                                                            toast.error(t('common.userInfoNotFound'));
                                                                            return;
                                                                        }
                                                                        dialogOpen({
                                                                            onExited: () => {
                                                                                revalidate();
                                                                            },
                                                                            innerElement: (
                                                                                <AccountInfoDialog
                                                                                    mallId={mallId || ''}
                                                                                    {...businessData}
                                                                                    userId={userId}
                                                                                />
                                                                            ),
                                                                        });
                                                                    }}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Box>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                            <CardContent>
                                                <LabeledTextList>
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        label={t('mall.bank')}
                                                        content={businessData?.settleBank || '-'}
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        label={t('mall.accountNumber')}
                                                        content={businessData?.settleAccountNumber || '-'}
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        label={t('mall.accountHolder')}
                                                        content={businessData?.settleAccountOwnerName || '-'}
                                                    />
                                                </LabeledTextList>
                                            </CardContent>
                                            <Divider />
                                            <div className="flex">
                                                <CardHeader
                                                    title={
                                                        <div className={'flex justify-between items-center'}>
                                                            <p>{t('mall.businessInfo')}</p>
                                                            <Box
                                                                sx={{
                                                                    height: '10px',
                                                                    overflowY: 'visible',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <IconButton
                                                                    onClick={() => {
                                                                        dialogOpen({
                                                                            onExited: () => {
                                                                                revalidate();
                                                                            },
                                                                            innerElement: (
                                                                                <BusinessInfoDialog
                                                                                    mallId={mallId || ''}
                                                                                    {...businessData}
                                                                                />
                                                                            ),
                                                                        });
                                                                    }}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Box>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                            <CardContent>
                                                <LabeledTextList>
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.companyName')}
                                                        content={businessData.companyName || '-'}
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.ownerName')}
                                                        content={businessData.ownerName || '-'}
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.businessRegistrationNumber')}
                                                        content={businessData.businessRegistrationNumber || '-'}
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.mailOrderSalesNumber')}
                                                        content={businessData.mailOrderSalesRegistrationNumber || '-'}
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.managerPhoneNumber')}
                                                        content={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <span>{businessData.managerPhoneNumber || '-'}</span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        dialogOpen({
                                                                            onExited: () => {
                                                                                revalidate();
                                                                            },
                                                                            innerElement: (
                                                                                <ManagerPhoneDialog
                                                                                    mallId={mallId || ''}
                                                                                    managerPhoneNumber={businessData.managerPhoneNumber}
                                                                                />
                                                                            ),
                                                                        });
                                                                    }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.companyAddress')}
                                                        content={businessData.companyAddress || '-'}
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.customerCenterNumber')}
                                                        content={businessData.customerCenterNumber || '-'}
                                                    />
                                                </LabeledTextList>
                                            </CardContent>
                                        </Card>
                                    );
                                }}
                            </Await>
                        </Suspense>
                    </Grid>
                    <Grid xs={12}>
                        <Card>
                            <div className="flex">
                                <CardHeader
                                    title={
                                        <div className={'flex justify-between items-center'}>
                                            <p>{t('mall.returnAddressSettings')}</p>
                                            <Box
                                                sx={{
                                                    height: '10px',
                                                    overflowY: 'visible',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <IconButton
                                                    onClick={() => {
                                                        dialogOpen({
                                                            onExited: () => {
                                                                revalidate();
                                                            },
                                                            innerElement: (
                                                                <ReturnAddressInfoDialog
                                                                    mallId={mallId || ''}
                                                                    postCode={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.postCode || ''
                                                                    }
                                                                    recipientAddressDetail={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.recipientAddressDetail || ''
                                                                    }
                                                                    recipientAddress={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.recipientAddress || ''
                                                                    }
                                                                    returnShippingCost={String(
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.returnShippingCost || 0,
                                                                    )}
                                                                    returnAddressName={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.returnAddressName || ''
                                                                    }
                                                                    returnAddressTel={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.returnAddressTel || ''
                                                                    }
                                                                />
                                                            ),
                                                        });
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Box>
                                        </div>
                                    }
                                />
                            </div>
                            <CardContent>
                                <LabeledTextList>
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.address')}
                                        content={brandInfoData?.mallReturnAddressInfo?.recipientAddress || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.detailedAddress')}
                                        content={brandInfoData?.mallReturnAddressInfo?.recipientAddressDetail || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.postalCode')}
                                        content={brandInfoData?.mallReturnAddressInfo?.postCode || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.returnAddressName')}
                                        content={brandInfoData?.mallReturnAddressInfo?.returnAddressName || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.returnAddressPhone')}
                                        content={brandInfoData?.mallReturnAddressInfo?.returnAddressTel || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.returnShippingCost')}
                                        content={
                                            !brandInfoData?.mallReturnAddressInfo?.returnShippingCost
                                                ? t('mall.freeShippingSellerBears')
                                                : `${Number(brandInfoData?.mallReturnAddressInfo?.returnShippingCost || 0).toLocaleString()}${t('common.koreanWon')}`
                                        }
                                    />
                                </LabeledTextList>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid xs={12}>
                        <Suspense fallback={<div>loading...</div>}>
                            <Await resolve={businessData}>
                                {(businessData) => {
                                    console.log(businessData);
                                    if (businessData.mallOpenBusinessType === 'SELLER') return;
                                    return (
                                        <Box display={'flex'} flexDirection={'column'} justifyContent={'center'}>
                                            <Box display={'flex'} alignItems={'center'}>
                                                {/* <InfoIcon color="error" sx={{ mr: 1 }} /> */}
                                                <Typography variant="h5">
                                                    {t('mall.businessRegistrationRecommendation')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    size="small"
                                                    color="black"
                                                    sx={{ bgcolor: 'lightgray' }}
                                                    variant="outlined"
                                                    onClick={() => {
                                                        dialogOpen({
                                                            innerElement: <TaxInfoDialog />,
                                                            fullWidth: true,
                                                        });
                                                    }}
                                                >
                                                    <Typography>
                                                        {t('mall.taxGuidanceForUnregisteredBusiness')}
                                                    </Typography>
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                }}
                            </Await>
                        </Suspense>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};

export default BusinessInfo;
